// =====================================
// VIDEO HELPER FUNCTIONS
// Location: src/lib/videoHelpers.ts
// =====================================
export interface ParsedVideo {
  type: "youtube" | "vimeo" | "descript" | "direct";
  id: string;
  embedUrl: string;
  thumbnailUrl?: string;
}

/**
 * Parse a video URL and return type, ID, and embed URL
 */
export function parseVideoUrl(url: string): ParsedVideo | null {
  if (!url) return null;

  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      const videoId = match[1];
      return {
        type: "youtube",
        id: videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      };
    }
  }

  // Vimeo patterns
  const vimeoPattern = /vimeo\.com\/(?:video\/)?(\d+)/;
  const vimeoMatch = url.match(vimeoPattern);
  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    return {
      type: "vimeo",
      id: videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
      // Vimeo thumbnails require API call, will be fetched separately if needed
      thumbnailUrl: undefined,
    };
  }

  // Descript patterns
  const descriptPattern = /share\.descript\.com\/view\/([a-zA-Z0-9_-]+)/;
  const descriptMatch = url.match(descriptPattern);
  if (descriptMatch) {
    const videoId = descriptMatch[1];
    return {
      type: "descript",
      id: videoId,
      embedUrl: url, // Use full URL for Descript embeds
      thumbnailUrl: undefined,
    };
  }

  // Direct video URLs (mp4, webm, etc.)
  const directVideoPattern = /\.(mp4|webm|ogg|mov)(\?.*)?$/i;
  if (directVideoPattern.test(url)) {
    return {
      type: "direct",
      id: url,
      embedUrl: url,
      thumbnailUrl: undefined,
    };
  }

  return null;
}

/**
 * Get thumbnail URL with fallback
 */
export function getThumbnailUrl(
  videoUrl: string,
  customThumbnail?: string
): string | undefined {
  // Custom thumbnail always takes priority
  if (customThumbnail) {
    return customThumbnail;
  }

  const parsed = parseVideoUrl(videoUrl);
  return parsed?.thumbnailUrl;
}

/**
 * Check if URL is a valid video URL
 */
export function isValidVideoUrl(url: string): boolean {
  return parseVideoUrl(url) !== null;
}

/**
 * Get video type from URL
 */
export function getVideoType(
  url: string
): "youtube" | "vimeo" | "descript" | "direct" | null {
  const parsed = parseVideoUrl(url);
  return parsed?.type || null;
}
